/**
 * The "Builder Layer" illustration from the website homepage's maintenance
 * section: integrations drift around a switched-off product until Ampersand
 * comes on, then its field pulls each one into place along the grid.
 *
 * Ported from `website/src/components/home/v2/maintenanceScene.ts`, keeping only
 * the builder scene. The artwork is authored against a fixed 640x400
 * coordinate space; `mount` letterboxes that box inside whatever the canvas
 * measures.
 */

const W = 640;
const H = 400;
const STEP = 24;
/** Routing lattice pitch: every node and cable corner lands on a multiple. */
const G = 12;

const DASH = [248, 255, 197] as const;
const LIME = [243, 248, 155] as const; // butter-300
const DENIM = [113, 175, 234] as const; // denim-400
const PAPER = [253, 251, 249] as const; // cream-100
const GREY = [158, 164, 168] as const;
const BG = "#2b2b2b"; // cream-900

const MONO = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const DOTO = '"Doto", ui-monospace, monospace';

/** Ampersand's symbol (a quarter ring and a square), from `website/src/components/home/v2/graphics/brand.ts`. */
const SYMBOL_RING =
  // eslint-disable-next-line max-len
  "M54.0487 48.3646C49.6765 48.3646 45.347 47.5034 41.3076 45.8302C37.2681 44.157 33.5978 41.7046 30.5061 38.6129C27.4145 35.5213 24.962 31.8509 23.2888 27.8115C21.6156 23.772 20.7544 19.4426 20.7544 15.0703L35.4039 15.0703C35.4039 17.5188 35.8862 19.9433 36.8232 22.2054C37.7602 24.4675 39.1335 26.5229 40.8649 28.2542C42.5962 29.9855 44.6516 31.3589 46.9137 32.2959C49.1758 33.2329 51.6003 33.7151 54.0487 33.7151V48.3646Z";
const SYMBOL_SQUARE = "M10 34.0703H24.3513V48.4216H10V34.0703Z";
/** The symbol's bounding box in the logo's own coordinates. */
const SYMBOL_BOX = { x: 10, y: 15.0703, w: 44.0487, h: 33.3513 };
/** The symbol's width on a disc, as a share of the diameter. */
const SYMBOL_IN_DISC = 0.58;
/** Diameter of the lime disc that carries the symbol inside "your product". */
const MARK_D = 34;

let symbolPaths: Path2D[] | null = null;

/** The symbol centred on (cx, cy), `w` wide, in one colour. */
function ampSymbol(ctx: CanvasRenderingContext2D, cx: number, cy: number, w: number, color: string) {
  if (!symbolPaths) symbolPaths = [new Path2D(SYMBOL_RING), new Path2D(SYMBOL_SQUARE)];
  const k = w / SYMBOL_BOX.w;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(k, k);
  ctx.translate(-(SYMBOL_BOX.x + SYMBOL_BOX.w / 2), -(SYMBOL_BOX.y + SYMBOL_BOX.h / 2));
  ctx.fillStyle = color;
  for (const p of symbolPaths) ctx.fill(p);
  ctx.restore();
}

type Rgb = readonly number[];
type Point = [number, number];

interface Stop {
  x: number;
  y: number;
}

interface BoxNode extends Stop {
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const smooth = (t: number) => {
  const u = clamp(t, 0, 1);
  return u * u * (3 - 2 * u);
};
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const rgba = (c: Rgb, a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const pulse = (now: number, t: number, dur = 450) => Math.max(0, 1 - (now - t) / dur);

function label(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  align: CanvasTextAlign = "left",
  size = 11,
  ls = 0.07,
) {
  ctx.font = `500 ${size}px ${MONO}`;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.fillStyle = color;
  ctx.letterSpacing = `${ls}em`;
  ctx.fillText(text, x, y);
  ctx.letterSpacing = "0px";
}

/** Where the live grid's first line sits, so a static grid can share its phase. */
function gridOrigin(cell: number, size: number) {
  const pad = Math.max(3 * cell, 48);
  const lines = Math.ceil((size + 2 * pad) / cell) + 1;
  return (size - (lines - 1) * cell) / 2;
}

/** The whole canvas in scene units, which is wider or taller than W x H when letterboxed. */
interface View {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Orthogonal cable router: A* on the lattice, 4 neighbours, corners penalised. */
function makeRouter(ox: number, oy: number) {
  const GC = Math.round((W - 2 * ox) / G) + 1;
  const GR = Math.round((H - 2 * oy) / G) + 1;
  const gi = (x: number) => Math.min(GC - 1, Math.max(0, Math.round((x - ox) / G)));
  const gj = (y: number) => Math.min(GR - 1, Math.max(0, Math.round((y - oy) / G)));

  function blockedFor(list: BoxNode[], ignore: BoxNode[]) {
    const b = new Uint8Array(GC * GR);
    const pad = 8;
    for (const n of list) {
      if (ignore.includes(n)) continue;
      const i0 = Math.max(0, Math.ceil((n.x - n.w / 2 - pad - ox) / G));
      const i1 = Math.min(GC - 1, Math.floor((n.x + n.w / 2 + pad - ox) / G));
      const j0 = Math.max(0, Math.ceil((n.y - n.h / 2 - pad - oy) / G));
      const j1 = Math.min(GR - 1, Math.floor((n.y + n.h / 2 + pad - oy) / G));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) b[j * GC + i] = 1;
    }
    return b;
  }

  function astar(sx: number, sy: number, tx: number, ty: number, blocked: Uint8Array): Point[] | null {
    const si = gi(sx);
    const sj = gj(sy);
    const ti = gi(tx);
    const tj = gj(ty);
    const N = GC * GR;
    const g = new Float32Array(N).fill(Infinity);
    const came = new Int32Array(N).fill(-1);
    const dirIn = new Int8Array(N).fill(-1);
    const closed = new Uint8Array(N);
    const heap: Array<[number, number]> = [];

    const push = (f: number, i: number) => {
      heap.push([f, i]);
      let k = heap.length - 1;
      while (k > 0) {
        const q = (k - 1) >> 1;
        if (heap[q][0] <= heap[k][0]) break;
        [heap[q], heap[k]] = [heap[k], heap[q]];
        k = q;
      }
    };
    const pop = () => {
      const top = heap[0];
      const last = heap.pop() as [number, number];
      if (heap.length) {
        heap[0] = last;
        let k = 0;
        for (;;) {
          const l = 2 * k + 1;
          const r = l + 1;
          let m = k;
          if (l < heap.length && heap[l][0] < heap[m][0]) m = l;
          if (r < heap.length && heap[r][0] < heap[m][0]) m = r;
          if (m === k) break;
          [heap[m], heap[k]] = [heap[k], heap[m]];
          k = m;
        }
      }
      return top;
    };

    const start = sj * GC + si;
    const goal = tj * GC + ti;
    g[start] = 0;
    push(Math.abs(si - ti) + Math.abs(sj - tj), start);
    const DX = [1, 0, -1, 0];
    const DY = [0, 1, 0, -1];
    while (heap.length) {
      const u = pop()[1];
      if (u === goal) break;
      if (closed[u]) continue;
      closed[u] = 1;
      const ui = u % GC;
      const uj = (u - ui) / GC;
      for (let d = 0; d < 4; d++) {
        const vi = ui + DX[d];
        const vj = uj + DY[d];
        if (vi < 0 || vi >= GC || vj < 0 || vj >= GR) continue;
        const v = vj * GC + vi;
        if (closed[v] || (blocked[v] && v !== goal)) continue;
        const ng = g[u] + 1 + (dirIn[u] !== -1 && dirIn[u] !== d ? 0.75 : 0);
        if (ng < g[v]) {
          g[v] = ng;
          came[v] = u;
          dirIn[v] = d;
          push(ng + Math.abs(vi - ti) + Math.abs(vj - tj), v);
        }
      }
    }
    if (goal !== start && came[goal] === -1) return null;

    const out: Point[] = [];
    for (let v = goal; v !== -1; v = came[v]) {
      out.push([ox + (v % GC) * G, oy + Math.floor(v / GC) * G]);
      if (v === start) break;
    }
    out.reverse();
    if (out.length < 3) return out;
    const c: Point[] = [out[0]];
    for (let k = 1; k < out.length - 1; k++) {
      const collinear = (out[k - 1][0] === out[k][0]) === (out[k][0] === out[k + 1][0]);
      if (!collinear) c.push(out[k]);
    }
    c.push(out[out.length - 1]);
    return c;
  }

  const elbow = (a: Stop, b: Stop): Point[] => [
    [a.x, a.y],
    [b.x, a.y],
    [b.x, b.y],
  ];

  function routeVia(list: BoxNode[], ignore: BoxNode[], ...stops: Stop[]): Point[] {
    const bl = blockedFor(list, ignore);
    let pts: Point[] = [[stops[0].x, stops[0].y]];
    for (let k = 1; k < stops.length; k++) {
      const seg = astar(stops[k - 1].x, stops[k - 1].y, stops[k].x, stops[k].y, bl) ?? elbow(stops[k - 1], stops[k]);
      pts = pts.concat(seg.slice(1));
    }
    return pts;
  }

  return routeVia;
}

interface Mass {
  x: number;
  y: number;
  m: number;
}

/** A mesh that bends towards the masses put on it; that bend *is* Ampersand's field. */
/**
 * The lattice covers `view` plus a margin, all on one phase. Nodes more than
 * `pad` outside the scene box never move, so widening the view only adds still
 * grid at the sides.
 */
function makeLiveGrid(cell: number, view: View) {
  const pad = Math.max(3 * cell, 48);
  const ox0 = gridOrigin(cell, W);
  const oy0 = gridOrigin(cell, H);
  const lastX0 = W - ox0;
  const lastY0 = H - oy0;
  const ox = ox0 - Math.max(0, Math.ceil((ox0 - view.x0) / cell)) * cell;
  const oy = oy0 - Math.max(0, Math.ceil((oy0 - view.y0) / cell)) * cell;
  const cols = Math.round((Math.max(lastX0, ox0 + Math.ceil((view.x1 - ox0) / cell) * cell) - ox) / cell) + 1;
  const rows = Math.round((Math.max(lastY0, oy0 + Math.ceil((view.y1 - oy0) / cell) * cell) - oy) / cell) + 1;
  const n = cols * rows;
  const gx = new Float32Array(n);
  const gy = new Float32Array(n);
  const X = new Float32Array(n);
  const Y = new Float32Array(n);
  const VX = new Float32Array(n);
  const VY = new Float32Array(n);
  const WT = new Float32Array(n);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      gx[i] = X[i] = ox + c * cell;
      gy[i] = Y[i] = oy + r * cell;
    }
  }
  const sm = (lo: number, hi: number, v: number) => smooth((v - lo) / (hi - lo || 1));

  /** Total displacement is saturated so the mesh can never fold over itself. */
  function step(dt: number, masses: Mass[], gain = 0.26, soft = 170) {
    const KS = 170;
    const drag = Math.exp(-12.6 * dt);
    const e2 = soft * soft;
    const cap = 0.5 * soft;
    for (let i = 0; i < n; i++) {
      const hx = gx[i];
      const hy = gy[i];
      let dx = 0;
      let dy = 0;
      let peak = 0;
      for (const m of masses) {
        const ax = m.x - hx;
        const ay = m.y - hy;
        const r2 = ax * ax + ay * ay + e2;
        const q = (Math.min(0.9, gain * m.m) * e2) / r2;
        dx += ax * q;
        dy += ay * q;
        const w = (m.m * e2) / r2;
        if (w > peak) peak = w;
      }
      const len = Math.hypot(dx, dy);
      if (len > 1e-6) {
        const f = 1 / (1 + len / cap);
        dx *= f;
        dy *= f;
      }
      const edge = Math.min(
        sm(0, pad, hx + pad),
        sm(0, pad, W + pad - hx),
        sm(0, pad, hy + pad),
        sm(0, pad, H + pad - hy),
      );
      WT[i] = (peak > 1 ? 1 : peak) * edge;
      const tx = hx + dx * edge;
      const ty = hy + dy * edge;
      const vx = (VX[i] + (tx - X[i]) * KS * dt) * drag;
      const vy = (VY[i] + (ty - Y[i]) * KS * dt) * drag;
      VX[i] = vx;
      VY[i] = vy;
      X[i] += vx * dt;
      Y[i] += vy * dt;
    }
  }

  function paint(
    ctx: CanvasRenderingContext2D,
    rgb: Rgb,
    base: number,
    lift: number,
  ) {
    const BANDS = 4;
    const bandOf = (i: number) => Math.min(BANDS - 1, (WT[i] * BANDS) | 0);
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.lineCap = "butt";
    for (let band = 0; band < BANDS; band++) {
      ctx.strokeStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${base + lift * (band / (BANDS - 1))})`;
      ctx.beginPath();
      for (let r = 0; r < rows; r++) {
        let open = false;
        for (let c = 0; c + 1 < cols; c++) {
          const i = r * cols + c;
          const j = i + 1;
          if (Math.max(bandOf(i), bandOf(j)) !== band) {
            open = false;
            continue;
          }
          if (!open) {
            ctx.moveTo(X[i], Y[i]);
            open = true;
          }
          ctx.lineTo(X[j], Y[j]);
        }
      }
      for (let c = 0; c < cols; c++) {
        let open = false;
        for (let r = 0; r + 1 < rows; r++) {
          const i = r * cols + c;
          const j = i + cols;
          if (Math.max(bandOf(i), bandOf(j)) !== band) {
            open = false;
            continue;
          }
          if (!open) {
            ctx.moveTo(X[i], Y[i]);
            open = true;
          }
          ctx.lineTo(X[j], Y[j]);
        }
      }
      ctx.stroke();
    }
  }

  /** True when the lattice no longer reaches every edge of `next`. */
  const covers = (next: View) =>
    ox <= next.x0 && oy <= next.y0 && ox + (cols - 1) * cell >= next.x1 && oy + (rows - 1) * cell >= next.y1;

  return { step, paint, covers };
}

function pathLen(pts: Point[]) {
  let L = 0;
  for (let k = 1; k < pts.length; k++) L += Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
  return L;
}

function pointAt(pts: Point[], s: number): Point {
  for (let k = 1; k < pts.length; k++) {
    const l = Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
    if (s <= l) {
      const t = l ? s / l : 0;
      return [lerp(pts[k - 1][0], pts[k][0], t), lerp(pts[k - 1][1], pts[k][1], t)];
    }
    s -= l;
  }
  return pts[pts.length - 1];
}

function strokePathOn(ctx: CanvasRenderingContext2D, pts: Point[], frac = 1) {
  let left = pathLen(pts) * frac;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let k = 1; k < pts.length && left > 0; k++) {
    const l = Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
    if (left >= l) {
      ctx.lineTo(pts[k][0], pts[k][1]);
      left -= l;
    } else {
      ctx.lineTo(lerp(pts[k - 1][0], pts[k][0], left / l), lerp(pts[k - 1][1], pts[k][1], left / l));
      left = 0;
    }
  }
  ctx.stroke();
}

interface Carrier {
  phase: number;
}

// ── scene data ──────────────────────────────────────────────────────────────

const BUILDER_NODES: Array<[string, number, number]> = [
  ["SALESFORCE", 104, 80],
  ["DYNAMICS", 536, 92],
  ["NETSUITE", 548, 308],
  ["HUBSPOT", 116, 320],
  ["ZENDESK", 320, 356],
  ["STRIPE", 536, 200],
  ["SLACK", 104, 200],
  ["WORKDAY", 320, 44],
  ["MARKETO", 200, 44],
  ["INTERCOM", 440, 356],
  ["SAP", 200, 356],
  ["GONG", 440, 44],
];
const BLD_T = 7.6;

/** Seconds into the loop that the reduced-motion still frame is composed at. */
const STILL_AT = 5;

const MIN_FRAME_MS = 1000 / 30 - 4;

export interface MountOptions {
  reducedMotion: boolean;
}

/** Draws the builder scene into `canvas` until the returned disposer is called. */
export function mountBuilderScene(canvas: HTMLCanvasElement, opts: MountOptions): () => void {
  const context = canvas.getContext("2d");
  if (!context) return () => {};
  const ctx: CanvasRenderingContext2D = context;

  const rm = opts.reducedMotion;
  const routeVia = makeRouter(8, 8);
  let view: View = { x0: 0, y0: 0, x1: W, y1: H };
  let bg = makeLiveGrid(STEP / 2, view);
  const center = { x: W / 2, y: H / 2 };

  let scale = 1;
  let offX = 0;
  let offY = 0;

  const boxW = (name: string) => {
    ctx.font = `700 12px ${DOTO}`;
    return Math.round((ctx.measureText(name).width + 24) / 2) * 2;
  };
  const blocker = (x: number, y: number, w: number, h: number): BoxNode => ({ name: "", x, y, w, h });
  const strokePath = (pts: Point[], frac?: number) => strokePathOn(ctx, pts, frac);

  function packet(pts: Point[], carrier: Carrier, dt: number, color: string, speed = 70) {
    const total = pathLen(pts);
    if (!total) return;
    carrier.phase = (carrier.phase + (rm ? 0 : (dt * speed) / total)) % 1;
    const [x, y] = pointAt(pts, carrier.phase * total);
    ctx.fillStyle = color;
    ctx.fillRect(x - 2.5, y - 2.5, 5, 5);
  }

  /** Every node is a pill with its name in Doto. */
  function box(n: BoxNode, stroke: string, fill: string, text: string, dashed?: boolean, lw = 1, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    if (dashed) ctx.setLineDash([2, 3]);
    ctx.fillStyle = fill;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.roundRect(n.x - n.w / 2, n.y - n.h / 2, n.w, n.h, n.h / 2);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = `700 12px ${DOTO}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = text;
    ctx.fillText(n.name, n.x, n.y + 0.5);
    ctx.restore();
  }

  /** Your product: grey and small while Ampersand is off, lime with the mark once on. */
  function product(r: number, k: number, glow = 0) {
    const ring = [
      Math.round(lerp(GREY[0], LIME[0], k)),
      Math.round(lerp(GREY[1], LIME[1], k)),
      Math.round(lerp(GREY[2], LIME[2], k)),
    ];
    ctx.fillStyle = BG;
    ctx.beginPath();
    ctx.arc(center.x, center.y, r, 0, 6.283);
    ctx.fill();
    ctx.strokeStyle = rgba(ring, lerp(0.55, 1, k));
    ctx.lineWidth = lerp(1, 1.5, k) + glow;
    ctx.beginPath();
    ctx.arc(center.x, center.y, r, 0, 6.283);
    ctx.stroke();
    if (k > 0.02) {
      ctx.setLineDash([2, 4]);
      ctx.strokeStyle = rgba(LIME, 0.5 * k);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(center.x, center.y, r + 8, 0, 6.283);
      ctx.stroke();
      ctx.setLineDash([]);
      // The mark stays a fixed size while the ring around it grows
      const ga = ctx.globalAlpha;
      ctx.globalAlpha = ga * k;
      ctx.fillStyle = rgba(LIME, 1);
      ctx.beginPath();
      ctx.arc(center.x, center.y, MARK_D / 2, 0, 6.283);
      ctx.fill();
      ampSymbol(ctx, center.x, center.y, MARK_D * SYMBOL_IN_DISC, "#000");
      ctx.globalAlpha = ga;
    }
    label(ctx, "YOUR PRODUCT", center.x, center.y + r + 18, rgba(PAPER, lerp(0.55, 0.9, k)), "center");
  }

  // ── Builder · pieces adrift until Ampersand's field locks them in ─────────

  interface Piece extends BoxNode, Carrier {
    sx: number;
    sy: number;
    vx: number;
    vy: number;
    i: number;
    on: boolean;
    was: boolean;
    a: number;
    locked: boolean;
    set: boolean;
    tLock: number;
    tSet: number;
    pts: Point[] | null;
    key: string;
  }

  const bld = {
    t0: 0,
    k: 0,
    s: 1,
    lastS: 1,
    ripple: -1,
    dirty: true,
    pieces: BUILDER_NODES.map(([name, sx, sy], i): Piece => ({
      name,
      sx,
      sy,
      x: sx + (Math.random() - 0.5) * 90,
      y: sy + (Math.random() - 0.5) * 70,
      vx: 0,
      vy: 0,
      w: boxW(name),
      h: 24,
      i,
      on: i < 5,
      was: i < 5,
      a: i < 5 ? 1 : 0,
      locked: false,
      set: false,
      tLock: -1e9,
      tSet: -1e9,
      pts: null,
      key: "",
      phase: Math.random(),
    })),
  };

  function renderBuilder(now: number, dt: number) {
    if (!bld.t0) bld.t0 = now;
    let t = (now - bld.t0) / 1000;
    if (t >= BLD_T) {
      bld.t0 = now;
      t = 0;
    }
    const on = t >= 0.8 && t < 6.4;
    bld.k += ((on ? 1 : 0) - bld.k) * (rm ? 1 : Math.min(1, dt * 4));
    const sT = on && t >= 2.6 ? lerp(1, 1.8, smooth((t - 2.6) / 1.6)) : 1;
    bld.s += (sT - bld.s) * (rm ? 1 : Math.min(1, dt * 6));
    if (Math.abs(bld.s - bld.lastS) > 0.04) {
      bld.lastS = bld.s;
      bld.dirty = true;
    }
    const r = lerp(24, 30 * bld.s, smooth(bld.k));
    bld.ripple = t >= 0.8 && t < 2.6 ? (t - 0.8) * 300 : -1;
    const want = on && t >= 2.6 ? Math.round(lerp(5, 12, smooth((t - 2.6) / 1.6))) : 5;

    bg.step(dt, [{ x: center.x, y: center.y, m: 0.7 * bld.k }]);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
    bg.paint(ctx, DASH, 0.06, 0.16 + 0.1 * bld.k);

    for (const p of bld.pieces) {
      p.on = p.i < want;
      if (p.on && !p.was) {
        const dx = p.sx - center.x;
        const dy = p.sy - center.y;
        const d = Math.hypot(dx, dy) || 1;
        p.x = p.sx + (dx / d) * 120;
        p.y = p.sy + (dy / d) * 120;
        p.locked = on;
        p.set = false;
      }
      p.was = p.on;
      p.a += ((p.on ? 1 : 0) - p.a) * (rm ? 1 : Math.min(1, dt * 3));

      if (!on) {
        if (p.locked) {
          p.locked = false;
          p.set = false;
          p.pts = null;
          p.key = "";
          const dx = p.x - center.x;
          const dy = p.y - center.y;
          const d = Math.hypot(dx, dy) || 1;
          p.vx = (dx / d) * 22;
          p.vy = (dy / d) * 18;
        }
        p.vx += ((Math.random() - 0.5) * 70 + (p.sx - p.x) * 0.5) * dt;
        p.vy += ((Math.random() - 0.5) * 70 + (p.sy - p.y) * 0.5) * dt;
        p.vx *= Math.exp(-0.6 * dt);
        p.vy *= Math.exp(-0.6 * dt);
        if (!rm) {
          p.x += p.vx * dt;
          p.y += p.vy * dt;
        }
        const dc = Math.hypot(p.x - center.x, p.y - center.y);
        if (dc < 96) {
          p.x = center.x + ((p.x - center.x) / dc) * 96;
          p.y = center.y + ((p.y - center.y) / dc) * 96;
        }
        p.x = clamp(p.x, 8 + p.w / 2, W - 8 - p.w / 2);
        p.y = clamp(p.y, 8 + p.h / 2, H - 8 - p.h / 2);
      } else {
        if (!p.locked && p.on && (bld.ripple < 0 || Math.hypot(p.x - center.x, p.y - center.y) <= bld.ripple)) {
          p.locked = true;
          p.tLock = now;
          p.vx = p.vy = 0;
        }
        if (p.locked) {
          const k = rm ? 1 : Math.min(1, dt * 7);
          p.x += (p.sx - p.x) * k;
          p.y += (p.sy - p.y) * k;
          if (!p.set && Math.abs(p.sx - p.x) + Math.abs(p.sy - p.y) < 1.5) {
            p.x = p.sx;
            p.y = p.sy;
            p.set = true;
            p.tSet = now;
            bld.dirty = true;
          }
        }
      }
    }

    if (bld.dirty) {
      const list: BoxNode[] = [
        ...bld.pieces.filter((p) => p.set && p.on),
        blocker(center.x, center.y + r + 22, 110, 20),
      ];
      for (const p of bld.pieces) {
        if (!(p.set && p.on)) {
          p.pts = null;
          continue;
        }
        const pts = routeVia(list, [p], center, p);
        const key = JSON.stringify(pts);
        if (key !== p.key) {
          p.pts = pts;
          p.key = key;
        }
      }
      bld.dirty = false;
    }

    ctx.lineJoin = "miter";
    for (const p of bld.pieces) {
      if (!p.pts || p.a <= 0) continue;
      const rv = rm ? 1 : Math.min(1, (now - p.tSet) / 350);
      ctx.strokeStyle = rgba(LIME, 0.9 * p.a);
      ctx.lineWidth = 1.4;
      strokePath(p.pts, rv);
      if (rv >= 1) packet(p.pts, p, dt, rgba(DENIM, p.a));
    }

    if (bld.ripple > 0) {
      const g = clamp(1 - bld.ripple / 500, 0, 1);
      ctx.strokeStyle = rgba(LIME, 0.7 * g);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(center.x, center.y, bld.ripple, 0, 6.283);
      ctx.stroke();
    }

    for (const p of bld.pieces) {
      if (p.a <= 0) continue;
      const lk = pulse(now, p.tLock, 600);
      box(
        p,
        rgba(p.locked ? (lk > 0 ? LIME : PAPER) : PAPER, p.locked ? 0.85 : 0.45),
        BG,
        rgba(PAPER, p.locked ? 0.95 : 0.55),
        !p.locked,
        1,
        p.a,
      );
      if (lk > 0) {
        ctx.strokeStyle = rgba(LIME, 0.6 * lk);
        ctx.lineWidth = 1;
        ctx.strokeRect(
          p.x - p.w / 2 - 3 - 6 * (1 - lk),
          p.y - p.h / 2 - 3 - 6 * (1 - lk),
          p.w + 6 + 12 * (1 - lk),
          p.h + 6 + 12 * (1 - lk),
        );
      }
    }

    product(r, smooth(bld.k));
  }

  // ── frame loop ────────────────────────────────────────────────────────────

  function resize() {
    const box = canvas.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const bw = Math.max(1, Math.round(box.width * dpr));
    const bh = Math.max(1, Math.round(box.height * dpr));
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
    }
    scale = Math.min(box.width / W, box.height / H) * dpr;
    offX = (bw - W * scale) / 2;
    offY = (bh - H * scale) / 2;
    view = {
      x0: -offX / scale,
      y0: -offY / scale,
      x1: (bw - offX) / scale,
      y1: (bh - offY) / scale,
    };
    if (!bg.covers(view)) bg = makeLiveGrid(STEP / 2, view);
  }

  function draw(now: number, dt: number) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, offX, offY);
    ctx.lineCap = "butt";
    renderBuilder(now, dt);
  }

  let raf = 0;
  let last = 0;
  let visible = true;

  const frame = (now: number) => {
    raf = requestAnimationFrame(frame);
    if (!visible) {
      last = now;
      return;
    }
    if (now - last < MIN_FRAME_MS) return;
    const dt = Math.min(0.05, (now - last) / 1000 || 0);
    last = now;
    draw(now, dt);
  };

  /** Reduced motion gets one frame, composed part-way into the loop so it says something. */
  const still = () => {
    const now = performance.now();
    bld.t0 = now - STILL_AT * 1000;
    draw(now, 0);
  };

  const ro = new ResizeObserver(() => {
    resize();
    if (rm) still();
  });
  ro.observe(canvas);

  let io: IntersectionObserver | null = null;
  resize();

  if (rm) {
    still();
  } else {
    io = new IntersectionObserver((entries) => {
      visible = entries.some((e) => e.isIntersecting);
    });
    io.observe(canvas);
    raf = requestAnimationFrame(frame);
  }

  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    io?.disconnect();
  };
}

/** Resolves once Doto is usable, so node pills are measured at their real width. */
export function whenDisplayFontReady(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return Promise.resolve();
  return document.fonts.load(`700 12px ${DOTO}`).then(
    () => undefined,
    () => undefined,
  );
}
